import React from 'react';
import { Alert, Button, Col, FloatingLabel, Form, Row } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Formik } from 'formik';
import * as Yup from 'yup';

import { DefaultSpinnerCenter } from '@components/Loader/Loader';
import { HTTP_400_BAD_REQUEST } from '@config/const';
import { bringInitialLoginState } from '@reducers/auth';
import { userApi } from '@reducers/user/user';

const schema = Yup.object({
  oldPassword: Yup.string().required('This is required field.'),
  newPassword: Yup.string().required('This is required field.'),
  newPasswordConfirm: Yup.string().required('This is required field.'),
});

export const PasswordErrors = ({ errors }) => {
  if (!errors) {
    return null;
  }
  if (!errors.passwordErrors) {
    return null;
  }
  return (
    <Alert variant="danger">
      {errors.passwordErrors.map((message) => (
        <Row key={message}>
          <Col>{message}</Col>
        </Row>
      ))}
    </Alert>
  );
};

export const ChangePasswordForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [trigger, { isLoading }] = userApi.useChangePasswordMutation();
  const onSubmit = async (values, actions) => {
    trigger(values)
      .unwrap()
      .then(() => {
        dispatch(bringInitialLoginState());
        navigate(`/login`);
      })
      .catch((response) => {
        const { status, data } = response;
        if (status === HTTP_400_BAD_REQUEST) {
          const {
            __all__: passwordErrors,
            old_password: oldPassword,
            new_password2: newPasswordConfirm,
          } = data;
          if (passwordErrors) {
            actions.setFieldError(
              'passwordErrors',
              passwordErrors.map(({ message }) => message)
            );
          }
          if (oldPassword) {
            actions.setFieldError(
              'oldPassword',
              oldPassword.map(({ message }) => message)[0]
            );
          }
          if (newPasswordConfirm) {
            actions.setFieldError(
              'newPasswordConfirm',
              newPasswordConfirm.map(({ message }) => message)[0]
            );
          }
          actions.setSubmitting(false);
        }
      });
  };

  return (
    <Formik
      enableReinitialize
      validationSchema={schema}
      initialValues={{
        oldPassword: '',
        newPassword: '',
        newPasswordConfirm: '',
      }}
      onSubmit={onSubmit}
    >
      {({
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        touched,
        errors,
      }) => (
        <>
          <PasswordErrors errors={errors} />
          <Form onSubmit={handleSubmit}>
            <FloatingLabel
              controlId="floatingOldPassword"
              label="Old password"
              className="mb-1 mt-3"
            >
              <Form.Control
                type="password"
                placeholder="Password"
                name="oldPassword"
                value={values.oldPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                isValid={touched.oldPassword && !errors.oldPassword}
                isInvalid={touched.oldPassword && errors.oldPassword}
              />
              <Form.Control.Feedback type="invalid">
                {errors.oldPassword}
              </Form.Control.Feedback>
            </FloatingLabel>
            <FloatingLabel
              controlId="floatingNewPassword"
              label="New password"
              className="mb-1"
            >
              <Form.Control
                type="password"
                placeholder="Password"
                name="newPassword"
                value={values.newPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                isValid={
                  touched.newPassword &&
                  !errors.newPassword &&
                  !errors.passwordErrors
                }
                isInvalid={
                  touched.newPassword &&
                  (errors.newPassword || errors.passwordErrors)
                }
              />
              <Form.Control.Feedback type="invalid">
                {errors.newPassword}
              </Form.Control.Feedback>
            </FloatingLabel>
            <FloatingLabel
              controlId="floatingConfirmPassword"
              label="New password confirmation"
              className="mb-1"
            >
              <Form.Control
                type="password"
                placeholder="Password"
                name="newPasswordConfirm"
                value={values.newPasswordConfirm}
                onChange={handleChange}
                onBlur={handleBlur}
                isValid={
                  touched.newPasswordConfirm &&
                  !errors.newPasswordConfirm &&
                  !errors.passwordErrors
                }
                isInvalid={
                  touched.newPasswordConfirm &&
                  (errors.newPasswordConfirm || errors.passwordErrors)
                }
              />
              <Form.Control.Feedback type="invalid">
                {errors.newPasswordConfirm}
              </Form.Control.Feedback>
            </FloatingLabel>
            <Button
              type="submit"
              className="w-100 mb-1"
              variant="didxl"
              disabled={isLoading}
            >
              Change {isLoading ? <DefaultSpinnerCenter /> : null}
            </Button>
          </Form>
        </>
      )}
    </Formik>
  );
};
